import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import LeadForm from "../components/LeadForm";
import FAQ from "../components/sections/FAQ";
import { COMPANY, CITIES, IMAGES } from "../lib/site";

const INFO = [
  { icon: Phone, label: "Call us", value: COMPANY.phone, href: COMPANY.phoneHref },
  { icon: Mail, label: "Email", value: COMPANY.email, href: `mailto:${COMPANY.email}` },
  { icon: MapPin, label: "Office address", value: COMPANY.address, href: COMPANY.mapUrl },
  { icon: Clock, label: "Hours", value: "Mon–Sat · 9:00 AM – 7:00 PM" },
];

export default function Contact() {
  return (
    <main data-testid="contact-page">
      <PageHero
        kicker="Contact us"
        title="Let's put your roof to work."
        subtitle="Request a free, no-obligation solar assessment. Our advisors respond within one business day with a system design, subsidy calculation and savings projection tailored to you."
        image={IMAGES.family}
      />

      <section id="quote" className="bg-cream py-24 md:py-32">
        <div className="mx-auto grid max-w-[1400px] gap-14 px-6 md:px-12 lg:grid-cols-[0.85fr_1.15fr]">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-gold">Reach us</p>
            <h2 className="mt-4 font-heading text-4xl font-extrabold tracking-tight text-forest md:text-5xl">Talk to a solar advisor.</h2>
            <div className="mt-10 space-y-4">
              {INFO.map((it) => {
                const Content = (
                  <div className="flex items-start gap-4 rounded-3xl border border-line bg-white/60 p-5">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-forest text-cream"><it.icon className="h-5 w-5" /></span>
                    <div>
                      <p className="text-xs uppercase tracking-widest text-moss">{it.label}</p>
                      <p className="mt-0.5 font-heading font-bold text-forest">{it.value}</p>
                    </div>
                  </div>
                );
                return it.href
                  ? <a key={it.label} href={it.href} className="block transition-transform hover:-translate-y-0.5">{Content}</a>
                  : <div key={it.label}>{Content}</div>;
              })}
            </div>
            <div className="mt-8">
              <p className="text-xs uppercase tracking-widest text-moss">We install in</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {CITIES.map((c) => (
                  <Link key={c.slug} to={`/solar/${c.slug}`} className="rounded-full border border-line px-4 py-1.5 text-sm text-moss transition-colors hover:border-forest hover:text-forest">{c.name}</Link>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="rounded-[2rem] border border-line bg-sand p-8 md:p-10">
              <h3 className="mb-6 font-heading text-2xl font-bold text-forest">Request your free quote</h3>
              <LeadForm sourcePage="Contact Page" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream pb-24 md:pb-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="mb-6 flex items-end justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-gold">Visit us</p>
              <h2 className="mt-3 font-heading text-3xl font-extrabold tracking-tight text-forest md:text-4xl">Our Mohali office</h2>
            </div>
            <a href={COMPANY.mapUrl} target="_blank" rel="noopener noreferrer"
              className="hidden shrink-0 rounded-full border border-forest px-6 py-3 text-sm font-semibold text-forest transition-colors hover:bg-forest hover:text-cream sm:inline-block">
              Open in Google Maps
            </a>
          </div>
          <div className="overflow-hidden rounded-[2rem] border border-line shadow-sm">
            <iframe title="Synergy Solar Inc. office location" src={COMPANY.mapEmbed}
              className="h-[380px] w-full md:h-[440px]" style={{ border: 0 }} loading="lazy"
              referrerPolicy="no-referrer-when-downgrade" allowFullScreen />
          </div>
        </div>
      </section>

      <FAQ />
    </main>
  );
}
